import { Card, makeStyles, tokens, Body1, Tooltip } from '@fluentui/react-components';
import {
  Info24Regular,
  Info20Regular,
  Warning24Regular,
  CheckmarkCircle24Regular,
  ArrowUpload24Regular,
  ArrowDownload24Regular,
  Play24Regular,
  CheckmarkCircle24Filled,
  ErrorCircle24Regular,
  CloudFlow24Regular,
  PersonAccounts24Regular,
  Building24Regular,
  BookOpen24Regular,
  People24Regular,
  ErrorCircle20Regular,
  Warning20Regular,
  Checkmark20Regular,
  ArrowRepeatAll20Regular,
} from '@fluentui/react-icons';
import type { LogEntry } from '../../types';
import { PayloadViewer } from './PayloadViewer';
import { extractErrorReason, getErrorFriendlyMessage, translateError } from '../../utils/errorTranslator';
import { formatOperation as formatOperationUtil } from '../../utils/operationFormatter';

interface TimelineEventProps {
  event: LogEntry;
  isFirstEvent?: boolean; // Para destacar o primeiro evento (API Received) em azul
}

const useStyles = makeStyles({
  eventCard: {
    marginBottom: '8px',
    borderLeft: `4px solid ${tokens.colorNeutralStroke1}`,
    transition: 'all 0.2s ease',
    position: 'relative',
    padding: '6px 10px',
    ':hover': {
      boxShadow: tokens.shadow8,
      transform: 'translateX(2px)',
    },
  },
  errorCard: {
    borderLeftColor: tokens.colorPaletteRedBorder2,
    backgroundColor: tokens.colorPaletteRedBackground1,
  },
  warningCard: {
    borderLeftColor: tokens.colorPaletteYellowBorder2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
  },
  successCard: {
    borderLeftColor: tokens.colorPaletteGreenBorder2,
    backgroundColor: tokens.colorPaletteGreenBackground1,
  },
  infoCard: {
    borderLeftColor: tokens.colorBrandStroke1,
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '40px 90px 120px 80px 180px 290px 1fr',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    width: '100%',
    fontSize: '13px',
  },
  correlationIdCell: {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
  },
  dateCell: {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
  },
  timestampCell: {
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: 'nowrap',
  },
  combinedInfoCell: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  retryInfoContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  nextRetryText: {
    color: '#666',
    fontSize: '11px',
    marginLeft: '4px',
  },
  badgeCell: {
    display: 'flex',
    justifyContent: 'center',
  },
  // Ícone de nível com fundo (mesmo padrão da tela de gerenciamento de fila)
  levelIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '5px',
    flexShrink: 0,
  },
  levelIconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  levelIconYellow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  levelIconGreen: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  levelIconBlue: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  levelIconGray: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
  },
  textCell: {
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  reasonCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  reasonLabel: {
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  reasonError: {
    color: tokens.colorNeutralForeground2,
    fontSize: '12px',
  },
  errorWithTooltip: {
    color: tokens.colorNeutralForeground2,
    cursor: 'help',
    textDecoration: 'underline dotted',
    fontSize: '12px',
  },
  tooltipContent: {
    padding: '8px',
    maxWidth: '350px',
  },
  tooltipHeader: {
    fontWeight: 600,
    marginBottom: '4px',
    fontSize: '13px',
  },
  tooltipMessage: {
    fontSize: '12px',
    opacity: 0.85,
    borderTop: '1px solid rgba(255,255,255,0.2)',
    paddingTop: '6px',
    marginTop: '6px',
    wordBreak: 'break-word' as const,
  },
});

export const TimelineEvent: React.FC<TimelineEventProps> = ({ event, isFirstEvent = false }) => {
  const styles = useStyles();

  // Determina o ícone baseado na operação e nível
  const getOperationIcon = () => {
    const operation = event.operation?.toUpperCase() || '';
    const message = event.message?.toUpperCase() || '';

    // Ícones por nível de log
    if (event.level === 'Error') {
      return <ErrorCircle24Regular />;
    }
    if (event.level === 'Warning') {
      return <Warning24Regular />;
    }

    // Ícones por tipo de operação
    if (operation.includes('API_RECEIVED')) {
      return <ArrowDownload24Regular />;
    }
    if (operation.includes('QUEUE_PUBLISHED')) {
      return <ArrowUpload24Regular />;
    }
    if (operation.includes('QUEUE_CONSUMED')) {
      return <CloudFlow24Regular />;
    }
    if (operation.includes('WORKER_STARTED')) {
      return <Play24Regular />;
    }
    if (operation.includes('WORKER_COMPLETED') || message.includes('COMPLETED')) {
      return <CheckmarkCircle24Filled />;
    }
    if (operation.includes('GRAPH_CALL_SUCCESS') || message.includes('SUCCESS')) {
      return <CheckmarkCircle24Regular />;
    }
    if (operation.includes('RETRY_SCHEDULED')) {
      return '🔄';
    }
    if (operation.includes('SENT_TO_DLQ')) {
      return <ErrorCircle24Regular />;
    }

    // Ícones por tipo de entidade
    const messageType = event.messageType?.toUpperCase() || '';
    if (messageType.includes('STUDENT') || messageType.includes('TEACHER') || messageType.includes('STAFF')) {
      return <PersonAccounts24Regular />;
    }
    if (messageType.includes('SCHOOL')) {
      return <Building24Regular />;
    }
    if (messageType.includes('CLASS')) {
      return <BookOpen24Regular />;
    }
    if (messageType.includes('ROSTER') || messageType.includes('ENROLLMENT')) {
      return <People24Regular />;
    }

    return <Info24Regular />;
  };

  // Determina o estilo do card
  const getCardStyle = () => {
    const message = event.message?.toUpperCase() || '';
    const operation = event.operation?.toUpperCase() || '';

    if (event.level === 'Error') {
      return `${styles.eventCard} ${styles.errorCard}`;
    }
    if (event.level === 'Warning') {
      return `${styles.eventCard} ${styles.warningCard}`;
    }
    if (
      message.includes('SUCCESS') ||
      message.includes('COMPLETED') ||
      operation.includes('SUCCESS') ||
      operation.includes('COMPLETED')
    ) {
      return `${styles.eventCard} ${styles.successCard}`;
    }
    return `${styles.eventCard} ${styles.infoCard}`;
  };

  // Ícone de nível com fundo - padronizado conforme tela de gerenciamento de fila
  const getLevelIcon = () => {
    const iconStyle = { width: 14, height: 14 };

    switch (event.level) {
      case 'Error':
        return (
          <div className={`${styles.levelIcon} ${styles.levelIconRed}`}>
            <ErrorCircle20Regular style={iconStyle} />
          </div>
        );
      case 'Warning':
        return (
          <div className={`${styles.levelIcon} ${styles.levelIconYellow}`}>
            <Warning20Regular style={iconStyle} />
          </div>
        );
      default:
        const message = event.message?.toUpperCase() || '';
        const operation = event.operation?.toUpperCase() || '';
        // Verifica SUCCESS/COMPLETED tanto em message quanto em operation
        if (
          message.includes('SUCCESS') || message.includes('COMPLETED') ||
          operation.includes('SUCCESS') || operation.includes('COMPLETED')
        ) {
          return (
            <div className={`${styles.levelIcon} ${styles.levelIconGreen}`}>
              <Checkmark20Regular style={iconStyle} />
            </div>
          );
        }
        // Primeiro evento (API Received) em azul com "i", outros Info em cinza com "i"
        if (isFirstEvent) {
          return (
            <div className={`${styles.levelIcon} ${styles.levelIconBlue}`}>
              <Info20Regular style={iconStyle} />
            </div>
          );
        }
        return (
          <div className={`${styles.levelIcon} ${styles.levelIconGray}`}>
            <Info20Regular style={iconStyle} />
          </div>
        );
    }
  };

  // Formata data (dd/MM/yyyy)
  const formatDate = () => {
    const timestamp = event.operationTimestamp || event.timeGenerated;
    const date = new Date(timestamp);
    return date.toLocaleDateString('pt-BR');
  };

  // Formata hora (HH:mm:ss.SSS)
  const formatTime = () => {
    const timestamp = event.operationTimestamp || event.timeGenerated;
    const date = new Date(timestamp);

    return date.toLocaleTimeString('pt-BR', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
    });
  };

  // Renderiza informações extras (retry badge ou razão de erro) - INLINE
  const renderExtraInfo = () => {
    const operation = event.operation?.toUpperCase() || '';
    const retryCount = event.retryCount ?? 0;
    const retryLimit = event.retryCountLimit ?? 12;

    // Mostra info de retry APENAS para RETRY_SCHEDULED (inline com data/hora)
    if (operation.includes('RETRY_SCHEDULED')) {
      const isLastAttempt = retryCount >= retryLimit;
      // retryCount já vem correto do Azure Search (1 = primeira tentativa)
      const badgeText = `Tentativa ${retryCount}/${retryLimit}`;

      // Se é a última tentativa, NÃO mostra data/hora da próxima
      const showNextRetry = !isLastAttempt && event.nextRetryTime;
      const nextRetryFormatted = showNextRetry && event.nextRetryTime
        ? new Date(event.nextRetryTime).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : '';

      return (
        <div className={styles.retryInfoContainer}>
          <ArrowRepeatAll20Regular style={{ color: tokens.colorNeutralForeground2, marginRight: '4px' }} />
          <span style={{ fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
            {badgeText}
          </span>
          {nextRetryFormatted && (
            <span className={styles.nextRetryText}>
              Próx: {nextRetryFormatted}
            </span>
          )}
        </div>
      );
    }

    // Para DLQ, mostra "Investigar" em vermelho
    if (operation.includes('SENT_TO_DLQ')) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className={styles.reasonError}>
            Investigar
          </span>
        </div>
      );
    }

    // Para eventos de erro (exceto RETRY_SCHEDULED e DLQ), mostra razão inline
    if (event.level === 'Error') {
      // Verifica se há dados estruturados de erro do Graph API
      // OU se tem errorMessage diretamente (do Azure Search)
      const hasStructuredError = event.errorStatusCode || event.errorCode || event.errorMessage;

      if (hasStructuredError) {
        // Identifica o recurso que causou o erro - APENAS userPrincipalName (UPN)
        // Só mostra se for um email válido (contém @)
        const resourceId = event.userPrincipalName && event.userPrincipalName.includes('@')
          ? event.userPrincipalName
          : null;

        // Usa a nova função com dados estruturados + messageType para mensagem específica
        const friendlyMessage = getErrorFriendlyMessage(
          event.errorCode,
          event.errorMessage,
          event.messageType,
          resourceId || undefined
        );

        // Tooltip com detalhes técnicos + UPN se disponível
        const tooltipContent = (
          <div className={styles.tooltipContent}>
            <div className={styles.tooltipHeader}>
              {event.errorStatusCode}: {event.errorCode}
            </div>
            {resourceId && (
              <div style={{ fontSize: '13px', fontWeight: 600, marginTop: '4px', color: '#60cdff' }}>
                {resourceId}
              </div>
            )}
            {event.errorMessage && (
              <div className={styles.tooltipMessage}>
                {event.errorMessage}
              </div>
            )}
          </div>
        );

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              content={tooltipContent}
              relationship="description"
              positioning="above"
            >
              <span className={styles.errorWithTooltip}>
                {friendlyMessage}
              </span>
            </Tooltip>
          </div>
        );
      }

      // Fallback para erros sem dados estruturados (compatibilidade)
      // Prioridade: event.reason (campo direto) > extractErrorReason (parsing) > exception > fallback
      const errorReason = event.reason || extractErrorReason(event);
      const friendlyReason = errorReason ? translateError(errorReason) : null;
      const displayMessage = friendlyReason || event.exception || 'Erro desconhecido';

      // Se tem reason, mostra tooltip com o texto original
      if (errorReason) {
        const tooltipContent = (
          <div className={styles.tooltipContent}>
            <div className={styles.tooltipHeader}>Erro de Validação</div>
            <div className={styles.tooltipMessage}>{errorReason}</div>
          </div>
        );

        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              content={tooltipContent}
              relationship="description"
              positioning="above"
            >
              <span className={styles.errorWithTooltip}>
                {displayMessage}
              </span>
            </Tooltip>
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className={styles.reasonError}>
            {displayMessage}
          </span>
        </div>
      );
    }

    // Para eventos normais, não mostra nada
    return null;
  };

  const operation = event.operation?.toUpperCase() || '';
  const isRetryScheduled = operation.includes('RETRY_SCHEDULED');
  const isDLQ = operation.includes('SENT_TO_DLQ');

  return (
    <Card className={getCardStyle()}>
      {/* Grid Horizontal: Ícone | Data | Hora | Badge | Operação | Email | Info Combinada (Retry/Razão + Payload) */}
      <div className={styles.gridContainer}>
        {/* Coluna 1: Ícone */}
        <div className={styles.iconCell}>{getOperationIcon()}</div>

        {/* Coluna 2: Data */}
        <div className={styles.dateCell}>{formatDate()}</div>

        {/* Coluna 3: Hora */}
        <div className={styles.timestampCell}>{formatTime()}</div>

        {/* Coluna 4: Ícone de nível (Info/Erro/Aviso) - DLQ sempre ícone Erro */}
        <div className={styles.badgeCell}>
          {isDLQ ? (
            <div className={`${styles.levelIcon} ${styles.levelIconRed}`}>
              <ErrorCircle20Regular style={{ width: 14, height: 14 }} />
            </div>
          ) : (
            getLevelIcon()
          )}
        </div>

        {/* Coluna 5: Operação */}
        <Body1 className={styles.textCell}>{formatOperationUtil(event.operation)}</Body1>

        {/* Coluna 6: CorrelationId */}
        <Body1 className={styles.correlationIdCell} title={event.correlationId}>
          {event.correlationId || '-'}
        </Body1>

        {/* Coluna 7: Info Combinada - TUDO INLINE (Retry/Razão + Payload) */}
        <div className={styles.combinedInfoCell}>
          {/* Parte 1: Retry badge ou Razão */}
          {renderExtraInfo()}

          {/* Parte 2: Payload Viewer (se disponível e não for retry) */}
          {!isRetryScheduled && event.payloadJson && (
            <PayloadViewer
              displayName={event.displayName}
              userPrincipalName={event.userPrincipalName}
              externalId={event.externalId}
              payloadJson={event.payloadJson}
              messageType={event.messageType}
              defaultExpanded={false}
            />
          )}
        </div>
      </div>
    </Card>
  );
};
